import React from 'react';
import { useCallback, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { STAGE_EVENTS } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import type { StagePointerEventT } from '../../../providers/stage-provider';
import type { SkeletonAnnotationT } from '../../../types';
import { isLabelingMode } from '../../toolbar/utils';
import { getTransformedSkeletonNodesOnVertexMove, getTranslatedSkeletonNodes } from '../utils';
import { LABEL_EDITOR_ELEMENT_NAME, LabelEditor } from './label-editor';
export const useUpdateSkeleton = ({
  annotation,
  annotationIdx,
  selectedVertexIdx,
  disableChanges,
  skeletonRef,
  setDrawingSkeleton,
  setSelectedVertexIdx
}: {
  annotation?: SkeletonAnnotationT;
  annotationIdx?: string;
  selectedVertexIdx: number | null;
  disableChanges?: boolean;
  skeletonRef: React.MutableRefObject<SVGGElement | null>;
  setDrawingSkeleton: (annotation: SkeletonAnnotationT | null) => void;
  setSelectedVertexIdx: (id: number | null) => void;
}) => {
  const {
    stage,
    updateAnnotation,
    renderElement,
    removeElement,
    getStageDimension
  } = useStage();
  const {
    editorModeAtom,
    editableAnnotationAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const {
    start,
    end
  } = useActionDuration();
  const isLocked = Boolean(annotation?.locked);
  const isEditable = editableAnnotation === annotationIdx;
  const getUpdatedNodeOnDrag = useCallback((stageEvent: StagePointerEventT, annotation: SkeletonAnnotationT) => {
    // if no vertex is select, then move the annotation
    if (selectedVertexIdx === null) {
      const [stageWidth, stageHeight] = getStageDimension();
      return getTranslatedSkeletonNodes(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation.data.nodes, stageWidth, stageHeight);
    }
    return getTransformedSkeletonNodesOnVertexMove(stageEvent.detail.mousePos, annotation.data.nodes, selectedVertexIdx);
  }, [selectedVertexIdx, getStageDimension]);
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    if (!annotation) {
      return;
    }
    if (selectedVertexIdx === null && stageEvent.detail.dragTarget?.parentElement !== skeletonRef.current) {
      return;
    }
    const updatedNodes = getUpdatedNodeOnDrag(stageEvent, annotation);
    setDrawingSkeleton({
      ...annotation,
      data: {
        ...annotation.data,
        nodes: updatedNodes
      }
    });
    start();
  }, [annotation, getUpdatedNodeOnDrag, selectedVertexIdx, setDrawingSkeleton, skeletonRef, start]);
  const onDragEnd = useCallback((stageEvent: StagePointerEventT) => {
    if (!annotation || !annotationIdx) {
      return;
    }
    if (selectedVertexIdx === null && stageEvent.detail.dragTarget?.parentElement !== skeletonRef.current) {
      return;
    }
    const updatedNodes = getUpdatedNodeOnDrag(stageEvent, annotation);
    updateAnnotation(annotationIdx, {
      ...annotation,
      data: {
        ...annotation.data,
        nodes: updatedNodes
      }
    }, {
      actionDuration: end(),
      vertex: updatedNodes.length
    });
    setSelectedVertexIdx(null);
  }, [annotation, annotationIdx, end, getUpdatedNodeOnDrag, selectedVertexIdx, setSelectedVertexIdx, skeletonRef, updateAnnotation]);
  const onRightClick = useCallback(() => {
    if (selectedVertexIdx === null || !annotation) {
      return;
    }
    const selectedNode = annotation.data.nodes[selectedVertexIdx];
    start();
    renderElement({
      name: LABEL_EDITOR_ELEMENT_NAME,
      element: <LabelEditor
      // @ts-expect-error todo(ts-migration) TS2322 Type 'NodeT | undefined' is not assignable to type 'NodeT'.
      selectedNode={selectedNode} onCancel={() => removeElement(LABEL_EDITOR_ELEMENT_NAME)} onUpdate={label => {
        removeElement(LABEL_EDITOR_ELEMENT_NAME);
        if (!annotationIdx) {
          return;
        }
        // @ts-expect-error todo(ts-migration) TS18048 'selectedNode' is possibly 'undefined'.
        const originalLabel = selectedNode.id;
        const updatedNodes = annotation.data.nodes.map((node, index) => index === selectedVertexIdx ? {
          ...node,
          id: label
        } : node);
        const updatedEdges = annotation.data.edges.map(edge => ({
          startNode: edge.startNode === originalLabel ? label : edge.startNode,
          endNode: edge.endNode === originalLabel ? label : edge.endNode
        }));
        const updatedAnnotation = {
          ...annotation,
          data: {
            nodes: updatedNodes,
            edges: updatedEdges
          }
        };
        updateAnnotation(annotationIdx, updatedAnnotation, {
          actionDuration: end(),
          vertex: updatedNodes.length
        });
      }} />
    });
  }, [annotation, annotationIdx, end, removeElement, renderElement, selectedVertexIdx, start, updateAnnotation]);
  useEffect(() => {
    if (!stage || disableChanges || isLocked || !isLabelingMode(editorMode)) return;
    if (!isEditable) {
      return;
    }
    stage.addEventListener(STAGE_EVENTS.DRAG, onDrag);
    stage.addEventListener(STAGE_EVENTS.DRAG_END, onDragEnd);
    stage.addEventListener(STAGE_EVENTS.RIGHT_CLICK, onRightClick);
    return () => {
      stage.removeEventListener(STAGE_EVENTS.DRAG, onDrag);
      stage.removeEventListener(STAGE_EVENTS.DRAG_END, onDragEnd);
      stage.removeEventListener(STAGE_EVENTS.RIGHT_CLICK, onRightClick);
    };
  }, [disableChanges, editorMode, isEditable, isLocked, onDrag, onDragEnd, onRightClick, stage]);
};